"use strict";
/*
 * Copyright 2023, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.manageRelocatingNodes = exports.getNodesInView = void 0;
const turf_1 = require("@turf/turf");
const MAX_NODE_RELOCATION_DISTANCE = 50; // in meters
const getNodesInView = (bounds, layer) => {
    const features = layer.features;
    const nodesInView = { type: 'FeatureCollection', features: [] };
    for (let i = 0; i < features.length; i++) {
        if (isInBounds(bounds, features[i].geometry.coordinates)) {
            nodesInView.features.push(features[i]);
        }
    }
    return nodesInView;
};
exports.getNodesInView = getNodesInView;
const isInBounds = (bounds, coord) => {
    return (0, turf_1.booleanPointInPolygon)(coord, bounds);
};
/**
Relocates nodes to the middle point of their crossing paths, if they intersect more than one path.
@param nodeFeatures - a FeatureCollection of nodes. (will be modified)
@param nodeMap - a Map of node IDs to arrays of path IDs that intersect the node.
@param pathFeatures - a FeatureCollection of paths.
@param maxDistance - the maximum manhattan distance between the old and new node coordinates for the relocation to occur.
*/
const relocateNodes = (nodeFeatures_1, nodeMap_1, pathFeatures_1, ...args_1) => __awaiter(void 0, [nodeFeatures_1, nodeMap_1, pathFeatures_1, ...args_1], void 0, function* (nodeFeatures, nodeMap, pathFeatures, isCancelled = false) {
    var _a;
    if (isCancelled && isCancelled()) {
        Promise.reject('Cancelled');
        return;
    }
    // Loop through each node feature in the input of the transit nodes array
    //nodeFeatures.features.forEach((nodeFeature, i) => {
    for (let i = 0; i < nodeFeatures.features.length; i++) {
        if (i % 20 === 0) {
            if (isCancelled && isCancelled()) {
                Promise.reject('Cancelled');
                return;
            }
        }
        // Get the ID of the current node.
        const nodeId = (_a = nodeFeatures.features[i].properties) === null || _a === void 0 ? void 0 : _a.id;
        // Get an array of the path IDs that intersect the current node.
        const paths = nodeMap.get(nodeId);
        // If the node intersects more than one path, find the middle point of the intersecting paths and relocate the node to that point.
        if (paths && paths.length > 1) {
            // Get an array of the coordinates of each path that intersects the node.
            const pathCoords = paths.map((pathId) => {
                const pathFeature = pathFeatures.features.find((feature) => feature.id === pathId);
                return pathFeature === null || pathFeature === void 0 ? void 0 : pathFeature.geometry.coordinates;
            });
            // Get the coordinates of the current node.
            const nodeCoords = nodeFeatures.features[i].geometry.coordinates;
            // Find the closest point on each path to the current node.
            const closestPoints = findClosestPoints(nodeCoords, pathCoords);
            // Find the middle point of the closest point.
            const middlePoint = findMiddlePoint(closestPoints);
            // Offset node if not too far from the old coordinates.
            if ((0, turf_1.distance)(nodeCoords, middlePoint, { units: 'meters' }) <= MAX_NODE_RELOCATION_DISTANCE) {
                nodeFeatures.features[i].geometry.coordinates = middlePoint;
            }
        }
    }
    return;
});
/**
 * Finds the point on each path that is closest to the given node.
 * @param nodeCoords - an array of two coordinates in the format [longitude, latitude] representing the coordinates of a node.
 * @param pathCoords - an array of arrays, where each inner array represents the coordinates of a path in the format [[longitude1, latitude1], [longitude2, latitude2], ...].
 * @returns an array of arrays, where each inner array represents the coordinates of the point on the corresponding path that is closest to the node.
 */
function findClosestPoints(nodeCoords, pathCoords) {
    const closestPoints = pathCoords.map((path) => {
        const line = (0, turf_1.lineString)(path);
        const nearestPoint = (0, turf_1.nearestPointOnLine)(line, nodeCoords);
        return nearestPoint.geometry.coordinates;
    });
    return closestPoints;
}
/**
 * Finds the middle point of an array of points.
 * @param points - an array of arrays, where each inner array represents a point in the format [longitude, latitude].
 * @returns an array representing the coordinates of the middle point of the input points.
 */
function findMiddlePoint(points) {
    const numPoints = points.length;
    const xCoords = points.map((point) => point[0]);
    const yCoords = points.map((point) => point[1]);
    const xSum = xCoords.reduce((sum, coord) => sum + coord, 0);
    const ySum = yCoords.reduce((sum, coord) => sum + coord, 0);
    const xMiddle = xSum / numPoints;
    const yMiddle = ySum / numPoints;
    return [xMiddle, yMiddle];
}
/**
 * Generates a Map object with the node IDs as keys and arrays of path IDs that intersect each node as values.
 * @param featureCollection - a GeoJSON FeatureCollection representing the transit paths.
 * @returns a Map object with the node IDs as keys and arrays of path IDs that intersect each node as values.
 */
function getCrossingPaths(featureCollection) {
    const nodeMap = new Map();
    featureCollection.features.forEach((feature) => {
        const nodes = feature.properties.nodes;
        nodes.forEach((node) => {
            if (!nodeMap.has(node)) {
                nodeMap.set(node, [feature.id]);
            }
            else {
                const paths = nodeMap.get(node);
                paths.push(feature.id);
                nodeMap.set(node, paths);
            }
        });
    });
    return nodeMap;
}
/**
 * Orchestrates the relocation of nodes by calling the relocateNodes function with the necessary parameters.
 * @param transitNodes - a Points feature collection giving a reference to the correct nodes layer (will be modified)
 * @param transitPath - a LineString feature collection giving a reference to the correct paths layer
 */
const manageRelocatingNodes = (transitNodes_1, transitPaths_1, ...args_1) => __awaiter(void 0, [transitNodes_1, transitPaths_1, ...args_1], void 0, function* (transitNodes, transitPaths, isCancelled = false) {
    try {
        const nodeMap = getCrossingPaths(transitPaths);
        yield relocateNodes(transitNodes, nodeMap, transitPaths, isCancelled);
        return;
    }
    catch (e) {
        return Promise.reject(e);
    }
});
exports.manageRelocatingNodes = manageRelocatingNodes;
//# sourceMappingURL=RelocateNodes.js.map